import { CombinedState, combineReducers, Reducer } from 'redux';
import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import authReducer from 'spw-redux/modules/auth/reducer';
import userReducer from 'spw-redux/modules/user/reducer';
import homeReducer from 'spw-redux/modules/home/reducer';
import benchmarkReportsReducer from 'spw-redux/modules/benchmark-reports/reducer';
import projectsReducer from 'spw-redux/modules/projects/reducer';
import projectSpaceTypeCategoriesReducer from 'spw-redux/modules/project-space-type-categories/reducer';
import libraryReducer from 'spw-redux/modules/library/reducer';
import floorplanReducer from 'spw-redux/modules/floorplan/reducer';
import programSummaryReducer from 'spw-redux/modules/program-summary/reducers';
import programmingProgramSummaryReducer from 'spw-redux/modules/program-summary/programming/reducer';
import ouReducer from 'spw-redux/modules/ou/reducer';
import calculatorReducer from 'spw-redux/modules/calculators/reducer';
import organizationsReducer from 'spw-redux/modules/organization/reducer';
import settingsReducer from 'spw-redux/modules/settings/reducer';
import buildingDetailReducer from 'spw-redux/modules/building-detail/reducer';
import feedbackReducer from 'spw-redux/modules/feedback/reducer';
import messagesReducer from 'spw-redux/modules/UI/messages/reducer';
import customSpacesReducer from 'spw-redux/modules/custom_spaces/reducer';
import spaceTypesReducer from 'spw-redux/modules/space_types/reducer';
import worksheetReducer from 'spw-redux/modules/worksheet/reducer';
import downloadsReducer from 'spw-redux/modules/downloads/reducer';
import usersReducer from 'spw-redux/modules/users/reducer';
import clientsReducer from 'spw-redux/modules/clients/reducer';
import teamsReducer from 'spw-redux/modules/teams/reducer';
import joinRequestsReducer from 'spw-redux/modules/join-requests/reducer';
import { IAuthState } from 'spw-redux/modules/auth/types';
import { IUserState } from 'spw-redux/modules/user/types';
import { IProjectsState } from 'spw-redux/modules/projects/types';
import { ILibraryState } from 'spw-redux/modules/library/types';
import {
  BulkSelectionState,
  IFloorplanState,
} from 'spw-redux/modules/floorplan/types';
import { IRoundsState } from 'spw-redux/modules/ou/types';
import { ICalculatorState } from 'spw-redux/modules/calculators/types';
import {
  IProgramSummaryState,
  ISeatsOccupied,
  IWorkplaceBehaviors,
} from 'spw-redux/modules/program-summary/types';
import { ICustomSpaceTypesState } from 'spw-redux/modules/custom_spaces/types';
import { IFeedbackState } from 'spw-redux/modules/feedback/types';
import { IMessagesState } from 'spw-redux/modules/UI/messages/types';
import { IBuildingDetail } from 'spw-redux/modules/building-detail/types';
import { ISpaceTypesState } from 'spw-redux/modules/space_types/types';
import { IWorksheetState } from 'spw-redux/modules/worksheet/types';
import { IProgrammingProgramSummaryState } from 'spw-redux/modules/program-summary/programming/types';
import { IProjectSpaceTypeCategoriesState } from 'spw-redux/modules/project-space-type-categories/types';
import { TWO_TOOLBARS_ERROR_MESSAGE } from 'spw-constants';
import { IOrganizationState } from './modules/organization/types';
import { ISettingsState } from './modules/settings/types';
import { IDownloadState } from './modules/downloads/types';
import { IUsersState } from './modules/users/types';
import { IClientsState } from './modules/clients/types';
import { IHomeState } from './modules/home/types';
import { ITeamsState } from './modules/teams/types';
import { IJoinRequestsState } from './modules/join-requests/types';
import { LOGOUT_SUCCEEDED } from './modules/auth/actions';
import { selectIsMeasuringUSF } from './modules/floorplan/selectors';
import { BenchmarkReportsState } from './modules/benchmark-reports/types';

export interface IAppState {
  authReducer: IAuthState;
  userReducer: IUserState;
  homeReducer: IHomeState;
  projectsReducer: IProjectsState;
  projectSpaceTypeCategoriesReducer: IProjectSpaceTypeCategoriesState;
  libraryReducer: ILibraryState;
  floorplanReducer: IFloorplanState;
  programSummaryReducer: IProgramSummaryState;
  programmingProgramSummaryReducer: IProgrammingProgramSummaryState;
  feedbackReducer: IFeedbackState;
  ouReducer: IRoundsState;
  messagesReducer: IMessagesState;
  workplaceBehavior: IWorkplaceBehaviors;
  seatsOccupied: ISeatsOccupied;
  customSpacesReducer: ICustomSpaceTypesState;
  calculatorReducer: ICalculatorState;
  buildingDetailReducer: IBuildingDetail;
  organizationsReducer: IOrganizationState;
  settingsReducer: ISettingsState;
  spaceTypesReducer: ISpaceTypesState;
  worksheetReducer: IWorksheetState;
  downloadsReducer: IDownloadState;
  usersReducer: IUsersState;
  clientsReducer: IClientsState;
  teamsReducer: ITeamsState;
  joinRequestsReducer: IJoinRequestsState;
  benchmarkReportsReducer: BenchmarkReportsState;
}

const projectsPersistConfig = {
  key: 'projectsReducer',
  storage,
  whitelist: ['previouslyVisitedBenchmarkProjectId'],
};

const floorplanReducerPersistConfig = {
  key: 'floorplanReducer',
  storage,
  whitelist: [
    'currentControlState',
    'oldControlState',
    'floorMapState',
    'measurementState',
  ],
};

const benchmarkReportsPersistConfig = {
  key: 'benchmarkReportsReducer',
  storage,
  whitelist: ['selectedProjects'],
};

const spaceTypesPersistConfig = {
  key: 'spaceTypesReducer',
  storage,
  whitelist: ['clientFacingNamesAreVisible'],
};

const libraryReducerPersistConfig = {
  key: 'libraryReducer',
  storage,
};

const persistedFloorplanReducer: typeof floorplanReducer = persistReducer(
  floorplanReducerPersistConfig,
  floorplanReducer
);

const persistedLibraryReducer: typeof libraryReducer = persistReducer(
  libraryReducerPersistConfig,
  libraryReducer
);

const persistedBenchmarkReportsReducer: typeof benchmarkReportsReducer = persistReducer(
  benchmarkReportsPersistConfig,
  benchmarkReportsReducer
);

const persistedProjectReducer: typeof projectsReducer = persistReducer(
  projectsPersistConfig,
  projectsReducer
);

const persistedSpaceTypesReducer: typeof spaceTypesReducer = persistReducer(
  spaceTypesPersistConfig,
  spaceTypesReducer
);

const allReducers: Reducer<CombinedState<Partial<IAppState>>> = combineReducers(
  {
    authReducer,
    userReducer,
    homeReducer,
    projectsReducer: persistedProjectReducer,
    projectSpaceTypeCategoriesReducer,
    libraryReducer: persistedLibraryReducer,
    floorplanReducer: persistedFloorplanReducer,
    programSummaryReducer,
    programmingProgramSummaryReducer,
    feedbackReducer,
    messagesReducer,
    ouReducer,
    customSpacesReducer,
    calculatorReducer,
    buildingDetailReducer,
    organizationsReducer,
    settingsReducer,
    spaceTypesReducer: persistedSpaceTypesReducer,
    worksheetReducer,
    downloadsReducer,
    usersReducer,
    clientsReducer,
    teamsReducer,
    joinRequestsReducer,
    benchmarkReportsReducer: persistedBenchmarkReportsReducer,
  }
);

const reducer = ((state, action) => {
  let _state:
    | typeof state
    | CombinedState<{ messagesReducer: IMessagesState }> = state;

  if (action.type === LOGOUT_SUCCEEDED) {
    const { messagesReducer: _messagesReducer } = state;
    // reset all other reducers to undefined except messagesReducer
    // so that logout reason message can be shown
    _state = { messagesReducer: _messagesReducer };
  }

  const newState = allReducers(_state, action) as IAppState;

  const isMeasuringUSF = selectIsMeasuringUSF(newState);
  const inSelectionMode =
    newState.floorplanReducer.bulkSelectionState ===
    BulkSelectionState.SELECTION;

  if (isMeasuringUSF && inSelectionMode) {
    throw new Error(TWO_TOOLBARS_ERROR_MESSAGE);
  }

  return newState;
}) as typeof allReducers;

export type RootState = ReturnType<typeof allReducers>;

export default reducer;
